import { FileText, Pencil, Plus, Save, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { type KeyedMutator } from 'swr';

import {
    AXISCARE_MEMO_SECTION_LABEL,
    isSupportedShiftNoteSource,
    shiftNoteSurfaceLabel,
    type SupportedShiftNoteSource,
    WELLSKY_NOTE_KIND_SECTIONS,
} from '@apps/web/components/shift_notes/shift_note_presentation';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import { formatFullDate, formatTime } from '@apps/web/lib/date_utils';
import {
    useOrganizationDataSourceAccounts,
    useShiftNotes,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type JoinedOutreachRead,
    type Shift,
    type ShiftNoteRead,
    type ShiftNotesResponse,
} from '@libraries/typescript/api_client';

type ShiftNotesModalButtonStyleProps = {
    readonly buttonVariant?: React.ComponentProps<typeof Button>['variant'];
    readonly buttonSize?: React.ComponentProps<typeof Button>['size'];
    readonly className?: string;
};

type ShiftNotesModalButtonProps = ShiftNotesModalButtonStyleProps &
    (
        | {
              readonly outreach: JoinedOutreachRead;
              readonly organizationId?: never;
              readonly shift?: never;
          }
        | {
              readonly outreach?: never;
              readonly organizationId: string;
              readonly shift: Shift;
          }
    );

type ShiftNoteKind = ShiftNoteRead['note_kind'];
type ShiftNotesMutate = KeyedMutator<ShiftNotesResponse>;

// The AxisCare memo write is optimistic-concurrency: a 409 means the live memo
// moved since we loaded it, and the backend returns the current value so we can
// recover in place instead of dead-ending on a stale local reload. FastAPI
// wraps a dict HTTPException detail as `{ detail: {...} }`.
function axisCareMemoConflict(
    error: unknown,
): { readonly currentMemo: string } | null {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return null;
    }
    const detail = (error.body as { readonly detail?: unknown } | undefined)
        ?.detail;
    if (
        detail !== null &&
        typeof detail === 'object' &&
        (detail as { readonly error?: unknown }).error ===
            'axiscare_memo_conflict' &&
        typeof (detail as { readonly current_memo?: unknown }).current_memo ===
            'string'
    ) {
        return {
            currentMemo: (detail as { readonly current_memo: string })
                .current_memo,
        };
    }
    return null;
}

// WellSky's note endpoint has no idempotency key, so a request that was accepted
// but whose response never arrived is indistinguishable from a clean rejection.
// Only a definite client-side rejection (a 4xx that isn't one of the specific
// cases) is safe to plainly retry; an ambiguous failure (network drop, timeout,
// or a non-verification 5xx) warns the coordinator to check WellSky first so a
// blind retry does not create a duplicate note.
// The route returns 409 for both the Activity-Note-ineligible case and a
// generic safety-validation failure, so we key off a structured detail
// discriminator (mirroring `axisCareMemoConflict`) rather than the status code
// — otherwise a genuine safety failure would wrongly read as "switch to Shift
// Note". Only the genuine, cleanly-read state ineligibility carries this.
function isWellSkyActivityNoteIneligible(error: unknown): boolean {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return false;
    }
    const detail = (error.body as { readonly detail?: unknown } | undefined)
        ?.detail;
    return (
        detail !== null &&
        typeof detail === 'object' &&
        (detail as { readonly error?: unknown }).error ===
            'wellsky_activity_note_ineligible'
    );
}

// The route returns 403 both when the note-writeback flag is off and when
// WellSky refused the account, so this keys off the structured discriminator
// rather than the status, exactly as the ineligibility check above does.
function isWellSkyPermissionDenied(error: unknown): boolean {
    if (!(error instanceof ApiError) || error.status !== 403) {
        return false;
    }
    const detail = (error.body as { readonly detail?: unknown } | undefined)
        ?.detail;
    return (
        detail !== null &&
        typeof detail === 'object' &&
        (detail as { readonly error?: unknown }).error ===
            'wellsky_permission_denied'
    );
}

function wellSkyNoteErrorMessage(
    error: unknown,
    noteKind: ShiftNoteKind,
): string {
    if (error instanceof ApiError) {
        if (noteKind === 'activity' && error.status === 502) {
            return 'WellSky accepted the request, but the shift or Activity Note could not be verified. Review this shift in WellSky before making another change.';
        }
        if (isWellSkyPermissionDenied(error)) {
            const detail = (
                error.body as
                    | {
                          readonly detail?: {
                              readonly message?: unknown;
                          };
                      }
                    | undefined
            )?.detail;
            if (typeof detail?.message === 'string') {
                return detail.message;
            }
            // Older API responses did not carry the classified message. Do
            // not turn their generic 403 into a permission claim.
            return 'WellSky did not confirm whether it saved this note and did not explain why. Check WellSky before trying again.';
        }
        if (isWellSkyActivityNoteIneligible(error)) {
            // Don't name a cause: an ordinary assigned scheduled shift is
            // ineligible without being recurring or already-worked.
            return 'This shift isn’t in a state where WellSky can take an Activity Note. Switched to Shift Note — your text is kept; press Add to WellSky to save it (it still shows on the shift’s notes).';
        }
        if (
            error.status !== undefined &&
            error.status >= 400 &&
            error.status < 500
        ) {
            return 'Failed to add shift note. Reload and try again.';
        }
    }
    return 'The note may have already been saved to WellSky. Check the shift in WellSky before adding it again to avoid a duplicate.';
}

// Optimistic-cache builders are pure over their inputs, so they live at module
// scope rather than re-closing over component state on every render.
function optimisticAxisCareMemo(
    current: ShiftNotesResponse | undefined,
    shiftId: string,
    body: string,
): ShiftNotesResponse | undefined {
    if (!current) {
        return current;
    }
    const existing = current.notes_by_shift_id[shiftId] ?? [];
    const existingMemo = existing.find(
        (note) => note.source_system === 'axiscare',
    );
    const now = new Date().toISOString();
    const memo: ShiftNoteRead = existingMemo
        ? { ...existingMemo, body, synced_at: now }
        : {
              id: `writeback-${shiftId}-${Date.now()}`,
              shift_id: shiftId,
              body,
              author: null,
              noted_at: null,
              occurrence_date: null,
              source_system: 'axiscare',
              note_kind: 'shift',
              synced_at: now,
          };
    return {
        notes_by_shift_id: {
            ...current.notes_by_shift_id,
            [shiftId]: [
                memo,
                ...existing.filter((note) => note.source_system !== 'axiscare'),
            ],
        },
        source_system_by_shift_id: current.source_system_by_shift_id ?? {},
    };
}

function optimisticWellSkyNote(
    current: ShiftNotesResponse | undefined,
    shiftId: string,
    body: string,
    noteKind: ShiftNoteKind,
): ShiftNotesResponse | undefined {
    if (!current) {
        return current;
    }
    const now = new Date().toISOString();
    return {
        notes_by_shift_id: {
            ...current.notes_by_shift_id,
            [shiftId]: [
                {
                    id: `writeback-${shiftId}-${Date.now()}`,
                    shift_id: shiftId,
                    body,
                    author: 'You',
                    noted_at: now,
                    occurrence_date: null,
                    source_system: 'wellsky',
                    note_kind: noteKind,
                    synced_at: now,
                },
                ...(current.notes_by_shift_id[shiftId] ?? []),
            ],
        },
        source_system_by_shift_id: current.source_system_by_shift_id ?? {},
    };
}

function NoteCard({
    note,
    timezone,
}: {
    readonly note: ShiftNoteRead;
    readonly timezone: string;
}) {
    // WellSky occurrence notes carry only a calendar date (stored at midnight),
    // so show the date without an invented clock time. AxisCare memos have no
    // author or timestamp.
    const notedDate = note.noted_at
        ? formatFullDate(note.noted_at, timezone)
        : null;
    return (
        <article className="space-y-2 rounded-md border p-3">
            {note.author || notedDate ? (
                <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                    {note.author ? <span>By {note.author}</span> : null}
                    {notedDate ? <span>{notedDate}</span> : null}
                </div>
            ) : null}
            <p className="break-words whitespace-pre-wrap">{note.body}</p>
        </article>
    );
}

function ShiftNoteSections({
    notes,
    timezone,
}: {
    readonly notes: ReadonlyArray<ShiftNoteRead>;
    readonly timezone: string;
}) {
    const wellskyNotes = notes.filter(
        (note) => note.source_system === 'wellsky',
    );
    const axisCareNotes = notes.filter(
        (note) => note.source_system === 'axiscare',
    );
    if (wellskyNotes.length === 0) {
        return (
            <div className="space-y-3">
                {axisCareNotes.map((note) => (
                    <NoteCard key={note.id} note={note} timezone={timezone} />
                ))}
            </div>
        );
    }
    return (
        <div className="space-y-4">
            {WELLSKY_NOTE_KIND_SECTIONS.map(({ kind, label }) => {
                const kindNotes = wellskyNotes.filter(
                    (note) => note.note_kind === kind,
                );
                if (kindNotes.length === 0) {
                    return null;
                }
                return (
                    <div key={kind} className="space-y-2">
                        <h4 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                            {label}
                        </h4>
                        <div className="space-y-3">
                            {kindNotes.map((note) => (
                                <NoteCard
                                    key={note.id}
                                    note={note}
                                    timezone={timezone}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
            {axisCareNotes.length > 0 ? (
                <div className="space-y-2">
                    <h4 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                        {AXISCARE_MEMO_SECTION_LABEL}
                    </h4>
                    <div className="space-y-3">
                        {axisCareNotes.map((note) => (
                            <NoteCard
                                key={note.id}
                                note={note}
                                timezone={timezone}
                            />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// AxisCare exposes one editable visit "Internal Memo" per shift. This editor
// owns its own draft/expected/saving state for the single shift it renders, so
// the parent modal keeps no per-shift edit maps.
// The synced notes list (or an empty-state line) for one shift. The parent
// renders it for every EHR so the write controls below stay display-agnostic.
function NotesOrEmpty({
    notes,
    timezone,
    surfaceLabel,
    groupNotesByShift,
    isMemoBeingEdited,
}: {
    readonly notes: ReadonlyArray<ShiftNoteRead>;
    readonly timezone: string;
    readonly surfaceLabel: string;
    readonly groupNotesByShift: boolean;
    // Whether this shift's AxisCare memo is open in the editor below.
    readonly isMemoBeingEdited: boolean;
}) {
    // The AxisCare editor holds the live memo itself, so rendering the synced
    // copy above it shows the same text twice and reads as though the edit is
    // going somewhere else. WellSky notes are append-only — the composer writes
    // a new note, so the existing ones stay on screen.
    const visibleNotes = isMemoBeingEdited
        ? notes.filter((note) => note.source_system !== 'axiscare')
        : notes;
    if (visibleNotes.length > 0) {
        return <ShiftNoteSections notes={visibleNotes} timezone={timezone} />;
    }
    // An open memo editor already shows the coordinator the (empty) memo this
    // line is about, so it only contradicts the textarea below it. The WellSky
    // composer files a separate new note, which leaves the synced list genuinely
    // empty — the line stays true there.
    if (groupNotesByShift && !isMemoBeingEdited) {
        return (
            <p className="text-muted-foreground text-sm">
                No synced {surfaceLabel.toLowerCase()}.
            </p>
        );
    }
    return null;
}

function AxisCareMemoEditor({
    shift,
    currentMemo,
    organizationId,
    onOpenChange,
    mutate,
}: {
    readonly shift: Shift;
    readonly currentMemo: string;
    readonly organizationId: string;
    readonly onOpenChange: (isEditing: boolean) => void;
    readonly mutate: ShiftNotesMutate;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [expectedBody, setExpectedBody] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const apiClient = getApiClient();

    // The editor owns its open state so a coordinator can have more than one
    // shift's memo open at once without losing a draft; the parent is only
    // told about it so it can drop its "nothing synced" copy for this shift.
    function setEditing(nextEditing: boolean) {
        setIsEditing(nextEditing);
        onOpenChange(nextEditing);
    }

    async function save() {
        // Saving an empty memo is a legitimate "clear the memo" operation, but
        // it overwrites the whole AxisCare Internal Memo — confirm first so a
        // coordinator does not wipe it by accident.
        if (
            draft.trim() === '' &&
            !window.confirm(
                'You’re about to clear the Internal Memo for this shift. Are you sure?',
            )
        ) {
            return;
        }
        setIsSaving(true);
        try {
            const result = await apiClient.put({
                url: '/organizations/{organization_id}/shifts/{shift_id}/memo',
                pathParams: {
                    organization_id: organizationId,
                    shift_id: shift.id,
                },
                body: { body: draft, expected_body: expectedBody },
            });
            if (!result.written) {
                toast.error('No AxisCare memo was written', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }
            await mutate(
                (current) => optimisticAxisCareMemo(current, shift.id, draft),
                { revalidate: false },
            );
            setEditing(false);
            toast.success('Internal Memo saved to AxisCare', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
        } catch (error) {
            console.error('Failed to save AxisCare Internal Memo:', error);
            const conflict = axisCareMemoConflict(error);
            if (conflict) {
                // The live memo moved under us. Show the current version in
                // place — both the draft and the optimistic-concurrency
                // baseline — so the coordinator can re-edit and save without
                // waiting for the next AxisCare sync. Keep the editor open.
                setDraft(conflict.currentMemo);
                setExpectedBody(conflict.currentMemo);
                toast.error(
                    'The Internal Memo changed since you opened it — the latest version is now shown. Review and save again.',
                    {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    },
                );
                return;
            }
            toast.error('Failed to save Internal Memo', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <React.Fragment>
            {isEditing ? (
                <div className="space-y-2">
                    <Textarea
                        aria-label="Internal Memo"
                        className="min-h-32"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(false)}
                            disabled={isSaving}
                        >
                            <X />
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => void save()}
                            disabled={isSaving}
                        >
                            {isSaving ? <Spinner tone="muted" /> : <Save />}
                            Save to AxisCare
                        </Button>
                    </div>
                </div>
            ) : null}
            {!isEditing ? (
                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setDraft(currentMemo);
                            setExpectedBody(currentMemo);
                            setEditing(true);
                        }}
                    >
                        <Pencil />
                        Edit Internal Memo
                    </Button>
                </div>
            ) : null}
        </React.Fragment>
    );
}

// WellSky notes are append-only: each save files a new "Note" or (guarded)
// "Activity Note". This composer owns its own draft/kind/saving state for the
// single shift it renders.
function WellSkyNoteComposer({
    shift,
    organizationId,
    mutate,
}: {
    readonly shift: Shift;
    readonly organizationId: string;
    readonly mutate: ShiftNotesMutate;
}) {
    const [isAdding, setIsAdding] = useState(false);
    const [draft, setDraft] = useState('');
    const [noteKind, setNoteKind] = useState<ShiftNoteKind>('shift');
    const [isSaving, setIsSaving] = useState(false);
    const apiClient = getApiClient();

    async function save() {
        const body = draft.trim();
        if (!body) {
            return;
        }
        setIsSaving(true);
        try {
            const result = await apiClient.post({
                url: '/organizations/{organization_id}/shifts/{shift_id}/notes',
                pathParams: {
                    organization_id: organizationId,
                    shift_id: shift.id,
                },
                body: { body, note_kind: noteKind },
            });
            if (!result.written) {
                toast.error('No WellSky note was written', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }
            await mutate(
                (current) =>
                    optimisticWellSkyNote(current, shift.id, body, noteKind),
                { revalidate: false },
            );
            setIsAdding(false);
            setDraft('');
            setNoteKind('shift');
            toast.success(
                noteKind === 'activity'
                    ? 'Activity Note added to WellSky'
                    : 'Shift note added to WellSky',
                {
                    persistNotification: true,
                    notificationType: 'shift_action',
                },
            );
        } catch (error) {
            console.error('Failed to add WellSky shift note:', error);
            const message = wellSkyNoteErrorMessage(error, noteKind);
            if (isWellSkyActivityNoteIneligible(error)) {
                // Preserve the draft and flip the type to Shift Note so the
                // coordinator can save the same text in one click.
                setNoteKind('shift');
                toast.warning(message, {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            } else {
                toast.error(message, {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            }
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <div className="space-y-2">
            {isAdding ? (
                <div className="space-y-3 rounded-md border p-3">
                    <div className="space-y-1">
                        <Label htmlFor={`wellsky-note-kind-${shift.id}`}>
                            Note type
                        </Label>
                        <Select
                            value={noteKind}
                            onValueChange={(value) =>
                                setNoteKind(value as ShiftNoteKind)
                            }
                        >
                            <SelectTrigger
                                id={`wellsky-note-kind-${shift.id}`}
                                className="w-full"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="shift">
                                    Shift Note
                                </SelectItem>
                                <SelectItem value="activity">
                                    Activity Note
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Textarea
                        aria-label="New WellSky shift note"
                        className="min-h-24"
                        placeholder="Add a shift note…"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setIsAdding(false)}
                            disabled={isSaving}
                        >
                            <X />
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => void save()}
                            disabled={isSaving || !draft.trim()}
                        >
                            {isSaving ? <Spinner tone="muted" /> : <Save />}
                            Add to WellSky
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setNoteKind('shift');
                            setIsAdding(true);
                        }}
                    >
                        <Plus />
                        Add note
                    </Button>
                </div>
            )}
        </div>
    );
}

export function ShiftNotesModalButton(props: ShiftNotesModalButtonProps) {
    const { buttonVariant = 'outline', buttonSize = 'sm', className } = props;
    const [open, setOpen] = useState(false);
    const { timezone } = useOrganizationTimezone();
    const currentOrganizationId = useOrganizationId();
    const organizationId = props.outreach
        ? currentOrganizationId
        : props.organizationId;
    const shifts = useMemo(
        () => (props.outreach ? props.outreach.shifts : [props.shift]),
        [props.outreach, props.shift],
    );
    const groupNotesByShift = props.outreach !== undefined;
    // Which shifts have their AxisCare memo open in the editor. The editors own
    // their open/draft state so a coordinator can work on several shifts at
    // once; this only mirrors it so the synced memo and the "nothing synced"
    // copy can step aside for the memo being edited. The WellSky composer needs
    // no equivalent — it files a new note and leaves the synced list as it is.
    const [shiftIdsWithMemoOpen, setShiftIdsWithMemoOpen] = useState<
        ReadonlySet<string>
    >(() => new Set());

    function setMemoOpen(shiftId: string, isOpen: boolean) {
        setShiftIdsWithMemoOpen((current) => {
            const next = new Set(current);
            if (isOpen) {
                next.add(shiftId);
            } else {
                next.delete(shiftId);
            }
            return next;
        });
    }
    const shiftIds = useMemo(
        () => Array.from(new Set(shifts.map((shift) => shift.id))),
        [shifts],
    );
    const { data, error, isLoading, mutate } = useShiftNotes(
        organizationId,
        shiftIds,
        {
            enabled: open,
            reason: 'shift-notes-modal',
        },
    );
    // Name the surface after the org's EHR. Accounts are a cached, shared
    // query, so this does not add a request in practice.
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        { reason: 'shift-notes-modal' },
    );
    const configuredSourceSystems = useMemo<
        ReadonlyArray<SupportedShiftNoteSource>
    >(() => {
        if (!dataSourceAccounts) {
            return [];
        }
        return Array.from(
            new Set(
                dataSourceAccounts
                    .map((account) => account.type)
                    .filter(isSupportedShiftNoteSource),
            ),
        );
    }, [dataSourceAccounts]);

    if (
        shiftIds.length === 0 ||
        dataSourceAccounts === undefined ||
        configuredSourceSystems.length === 0
    ) {
        return null;
    }

    const shiftSourceSystems = Array.from(
        new Set(
            Object.values(data?.source_system_by_shift_id ?? {}).filter(
                (sourceSystem): sourceSystem is SupportedShiftNoteSource =>
                    sourceSystem !== null &&
                    isSupportedShiftNoteSource(sourceSystem),
            ),
        ),
    );
    const ehrSourceSystem =
        shiftSourceSystems.length === 1
            ? (shiftSourceSystems[0] ?? null)
            : configuredSourceSystems.length === 1
              ? (configuredSourceSystems[0] ?? null)
              : null;
    const surfaceLabel = shiftNoteSurfaceLabel(ehrSourceSystem);
    const allNotesEmpty =
        data !== undefined &&
        shiftIds.every(
            (shiftId) => (data.notes_by_shift_id[shiftId]?.length ?? 0) === 0,
        );
    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    // Closing unmounts the editors, so they never report
                    // themselves closed.
                    setShiftIdsWithMemoOpen(new Set());
                }
            }}
        >
            <Button
                variant={buttonVariant}
                size={buttonSize}
                className={className}
                onClick={() => setOpen(true)}
            >
                <FileText />
                {surfaceLabel}
            </Button>
            <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{surfaceLabel}</DialogTitle>
                    <DialogDescription>
                        Synced {surfaceLabel.toLowerCase()} from{' '}
                        {ehrSourceSystem
                            ? getDataSourceDisplayName(ehrSourceSystem)
                            : 'the EHR'}
                        {groupNotesByShift ? ', grouped by shift' : null}.
                    </DialogDescription>
                </DialogHeader>
                {isLoading ? (
                    <div
                        className="text-muted-foreground flex min-h-32 items-center justify-center gap-2"
                        role="status"
                    >
                        <Spinner tone="muted" />
                        Loading {surfaceLabel.toLowerCase()}…
                    </div>
                ) : error ? (
                    <div className="flex min-h-32 flex-col items-center justify-center gap-3 text-center">
                        <p className="text-destructive">
                            {surfaceLabel} couldn’t be loaded.
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void mutate()}
                        >
                            Try again
                        </Button>
                    </div>
                ) : data ? (
                    <div
                        className="min-h-0 flex-1 overflow-y-auto pr-4 max-sm:-mr-4 sm:-mr-6 sm:pr-6"
                        aria-label={`${surfaceLabel} by shift`}
                    >
                        <div className="space-y-5 pb-1">
                            {allNotesEmpty &&
                            !groupNotesByShift &&
                            shiftIdsWithMemoOpen.size === 0 ? (
                                <p className="bg-muted text-muted-foreground rounded-md p-3 text-sm">
                                    No synced {surfaceLabel.toLowerCase()}{' '}
                                    {surfaceLabel === 'Memo' ? 'is' : 'are'}{' '}
                                    available.
                                </p>
                            ) : null}
                            {shifts.map((shift) => {
                                const notes =
                                    data.notes_by_shift_id[shift.id] ?? [];
                                const axisCareMemo =
                                    notes.find(
                                        (note) =>
                                            note.source_system === 'axiscare',
                                    )?.body ?? '';
                                const rawShiftSourceSystem =
                                    data.source_system_by_shift_id?.[shift.id];
                                const shiftSourceSystem =
                                    rawShiftSourceSystem &&
                                    isSupportedShiftNoteSource(
                                        rawShiftSourceSystem,
                                    )
                                        ? rawShiftSourceSystem
                                        : ehrSourceSystem;
                                return (
                                    <section
                                        key={shift.id}
                                        className="space-y-3"
                                    >
                                        {groupNotesByShift ? (
                                            <div>
                                                <h3 className="font-medium">
                                                    {shift.patient_name}
                                                </h3>
                                                <p className="text-muted-foreground text-sm">
                                                    {formatFullDate(
                                                        shift.shift_start_time,
                                                        timezone,
                                                    )}{' '}
                                                    ·{' '}
                                                    {formatTime(
                                                        shift.shift_start_time,
                                                        timezone,
                                                    )}{' '}
                                                    –{' '}
                                                    {formatTime(
                                                        shift.shift_end_time,
                                                        timezone,
                                                    )}
                                                </p>
                                            </div>
                                        ) : null}
                                        <NotesOrEmpty
                                            notes={notes}
                                            timezone={timezone}
                                            surfaceLabel={surfaceLabel}
                                            groupNotesByShift={
                                                groupNotesByShift
                                            }
                                            isMemoBeingEdited={shiftIdsWithMemoOpen.has(
                                                shift.id,
                                            )}
                                        />
                                        {shiftSourceSystem === 'axiscare' &&
                                        organizationId ? (
                                            <AxisCareMemoEditor
                                                shift={shift}
                                                currentMemo={axisCareMemo}
                                                organizationId={organizationId}
                                                onOpenChange={(nextEditing) =>
                                                    setMemoOpen(
                                                        shift.id,
                                                        nextEditing,
                                                    )
                                                }
                                                mutate={mutate}
                                            />
                                        ) : shiftSourceSystem === 'wellsky' &&
                                          organizationId ? (
                                            <WellSkyNoteComposer
                                                shift={shift}
                                                organizationId={organizationId}
                                                mutate={mutate}
                                            />
                                        ) : null}
                                    </section>
                                );
                            })}
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
